import { msg } from '@lingui/macro'
import {
  Model,
  StackProjections,
  getBottomWallStackId,
  getCountertopWallStackId,
  getEmptySpace,
  getTopWallStackId,
} from 'jms-domain'
import { BottomWallItem, CountertopWallItem, TopWallItem } from 'jms-domain/src/__generated/model'
import type { PropsWithChildren } from 'react'
import { useEffect } from 'react'
import { match } from 'ts-pattern'

import { configuratorInterface } from '../systems/configurator-interface'

export const AssignWallItemToZoneTransformerComponent = ({ children }: PropsWithChildren) => {
  const domainContext = configuratorInterface.useDomain()

  // TODO: we need systems in the transformer buidler - eg. component reader
  useEffect(() => {
    const transformerRegistration = configuratorInterface.transformerBuilder.when
      .entity(Model.WallItem.types)
      .created()
      .then.applySideEffect(ctx => {
        const $root = ctx.getOld$Root()

        const stackProjections = new StackProjections(domainContext.componentReader)

        const committedState = ctx.getOldState()
        const createdEntity = ctx.getNew$Entity()

        const getStackKey = match(createdEntity)
          .when(TopWallItem.is, () => getTopWallStackId)
          .when(BottomWallItem.is, () => getBottomWallStackId)
          .when(CountertopWallItem.is, () => getCountertopWallStackId)
          .otherwise(() => {
            throw new Error('Invalid wall item type!')
          })

        return match($root.shape)
          .with('I', () => {
            const stackB = stackProjections.getStack(getStackKey($root.$wallB.id), committedState)

            const space = getEmptySpace(stackB, createdEntity.$width, { min: 0, max: Infinity })

            if (space == null) {
              throw new Error('WallItem does not fit in the zone!')
            }

            return [
              configuratorInterface.updates.DomainEntityModification({
                entityId: createdEntity.id,
                stateDelta: {
                  x: space?.start ?? 0,
                  wallId: $root.$wallB.id,
                },
                updateTag: 'assignWallItemToZone',
                hint: space.end > $root.$wallB.length ? msg`This WallItem doesn't fit in the room anymore.` : undefined,
              }),
            ]
          })
          .with('L', () => {
            const stackA = stackProjections.getStack(getStackKey($root.$wallA.id), committedState)

            const spaceA = getEmptySpace(stackA, createdEntity.$width, {
              min: $root.$wallB.$maxWallItemDepth,
              max: stackA.limits.max,
            })

            if (spaceA) {
              return [
                configuratorInterface.updates.DomainEntityModification({
                  entityId: createdEntity.id,
                  stateDelta: {
                    x: spaceA.start,
                    wallId: $root.$wallA.id,
                  },
                  updateTag: 'assignWallItemToZone',
                }),
              ]
            }

            const stackB = stackProjections.getStack(getStackKey($root.$wallB.id), committedState)

            const spaceB = getEmptySpace(stackB, createdEntity.$width, { min: 0, max: Infinity })

            if (spaceB == null) {
              throw new Error('WallItem does not fit in the zone!')
            }

            return [
              configuratorInterface.updates.DomainEntityModification({
                entityId: createdEntity.id,
                stateDelta: {
                  x: spaceB.start,
                  wallId: $root.$wallB.id,
                },
                updateTag: 'assignWallItemToZone',
                hint:
                  spaceB.end > $root.$wallB.length - $root.$wallA.$maxWallItemDepth
                    ? msg`This WallItem doesn't fit in the room anymore.`
                    : undefined,
              }),
            ]
          })
          .with('U', () => {
            const stackA = stackProjections.getStack(getStackKey($root.$wallA.id), committedState)

            const spaceA = getEmptySpace(stackA, createdEntity.$width, {
              min: $root.$wallB.$maxWallItemDepth,
              max: stackA.limits.max,
            })

            if (spaceA) {
              return [
                configuratorInterface.updates.DomainEntityModification({
                  entityId: createdEntity.id,
                  stateDelta: {
                    x: spaceA.start,
                    wallId: $root.$wallA.id,
                  },
                  updateTag: 'assignWallItemToZone',
                }),
              ]
            }

            const stackB = stackProjections.getStack(getStackKey($root.$wallB.id), committedState)

            const spaceB = getEmptySpace(stackB, createdEntity.$width)

            if (spaceB) {
              return [
                configuratorInterface.updates.DomainEntityModification({
                  entityId: createdEntity.id,
                  stateDelta: {
                    x: spaceB.start,
                    wallId: $root.$wallB.id,
                  },
                  updateTag: 'assignWallItemToZone',
                }),
              ]
            }

            const stackC = stackProjections.getStack(getStackKey($root.$wallC.id), committedState)

            const spaceC = getEmptySpace(stackC, createdEntity.$width)

            if (!spaceC) {
              throw new Error('WallItem does not fit in the zone!')
            }

            return [
              configuratorInterface.updates.DomainEntityModification({
                entityId: createdEntity.id,
                stateDelta: {
                  x: spaceC.start,
                  wallId: $root.$wallC.id,
                },
                updateTag: 'assignWallItemToZone',
                hint:
                  spaceC.end > $root.$wallC.length - $root.$wallB.$maxWallItemDepth
                    ? msg`This WallItem doesn't fit in the room anymore.`
                    : undefined,
              }),
            ]
          })
          .exhaustive()
      })
      .build('assign-wall-item-to-zone-transformer')
      .register(domainContext)

    const unregister = domainContext.storeSystem.registerTransformer(
      // TODO: fix SDK bug
      10000,
      transformerRegistration.transformer,
      transformerRegistration.name!
    )

    return () => {
      unregister()
    }
  }, [domainContext])

  return <>{children}</>
}
